import FingerprintJS from '@fingerprintjs/fingerprintjs'
import getCurrencies from 'api/currencies'
import getEmailsBlackList from 'api/emailsBlackList'
import register from 'api/register'
import { PlausibleEvent, RegistrationTypes } from 'enums/plausible'
import { Formik } from 'formik'
import handleResponseError from 'forms/handleResponseError'
import Cookies from 'js-cookie'
import { map } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { currenciesWithTranslations } from 'static/currencies'
import getReferralCookies, { clearReferralCookies } from 'utils/getReferralCookies'
import useAntifraudSDK from 'utils/hooks/useAntifraudSDK'
import useLanguageForSave from 'utils/hooks/useLanguageForSave'
import usePlausible from 'utils/hooks/usePlausible'
// import onlyAllowedAndLatinLetters from 'utils/onlyAllowedAndLatinLetters'
import setTokensAndRedirect from 'utils/setTokensAndRedirect'
import * as yup from 'yup'
import ErrorCode from './ErrorCodes'
import RegisterFormInner from './RegisterFormInner'
import validateMessage from './validateMessage'

import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import { EmailIcon, InvalidEmailIcon, WiFiIcon } from 'components/Images/Icons'

const latinWithSpecialSymbols = /^[a-zA-Z0-9!@#$%^&*()_+{}[\]:;"'<>,.?\\|\-=]+$/

const RegisterForm = ({ referrer, error, setError, setSocialRegistrationError }) => {
  const [currencies, setCurrencies] = useState([])
  const [isCurrenciesloading, setIsCurrenciesLoading] = useState(false)
  const [emailsBlackList, setEmailsBlackList] = useState([])
  const [t, i18n] = useTranslation(['main', 'error'])
  const router = useRouter()
  const setEvent = usePlausible()
  const savedLanguage = useLanguageForSave(i18n)
  const { initAntifraudSession } = useAntifraudSDK()
  const [isLoading, toggleIsLoading] = useState(false)
  const [isSucceed, toggleIsSucceed] = useState(false)

  useEffect(() => {
    const fetchCurrencies = async () => {
      setIsCurrenciesLoading(true)
      try {
        const res = await getCurrencies()
        const items = Object.values(res?.data?.payload?.items) || []
        setCurrencies(items)
      } catch (e) {
        console.error(e)
      } finally {
        setIsCurrenciesLoading(false)
      }
    }
    fetchCurrencies()
  }, [])

  useEffect(() => {
    const fetchEmailsBlackList = async () => {
      const res = await getEmailsBlackList()
      const items = map(Object.values(res?.data?.payload?.items), 'domain') || []
      setEmailsBlackList(items)
    }
    fetchEmailsBlackList()
  }, [])

  const currencyFieldValues = useMemo(() => {
    return currencies
      .filter((item) => item.isCanRegistered)
      .map((item) => {
        const translationKey = currenciesWithTranslations.find(
          (c) => c.code === item.code,
        )?.translationKey
        return {
          name: translationKey ? t(translationKey) : item.name,
          value: item.id,
          icon: item.icon,
          label: item.code,
          countries: item.countries,
        }
      })
  }, [currencies, t])

  const onRegister = useCallback(
    async (values) => {
      setSocialRegistrationError(undefined)
      toggleIsLoading(true)

      setEvent(PlausibleEvent.ClickedSignUp, {
        step: 2,
        referrer,
        source: router.query?.from as string,
        currency: values.currency.value,
        email: values.email,
      })
      setEvent(PlausibleEvent.SubmitRegister, {
        type: `user`,
        appName: 'mainLanding',
        referrer,
      })
      try {
        const sessionId = await initAntifraudSession(`register-${Date.now()}`, 'register')

        const fp = await FingerprintJS.load()
        const fpResponse = await fp.get()

        const res = await register({
          email: values.email,
          password: values.password,
          preferredCurrency: values.currency.value,
          language: savedLanguage,
          visitorId: fpResponse.visitorId,
          sessionId: sessionId || undefined,
          ...getReferralCookies(),
        })
        if (res.data.status !== 'success') {
          const { code, payload } = res.data
          switch (code) {
            case ErrorCode.VALIDATION_ERRORS:
              if (payload?.errors) {
                const wrappedErrors = handleResponseError(res)
                if (wrappedErrors.email) {
                  setError({ icon: EmailIcon, message: wrappedErrors.email[0] })
                } else {
                  setError({ message: wrappedErrors })
                }
              }
              break
            case ErrorCode.EMAIL_ALREADY_REGISTER:
              setError({ icon: EmailIcon, message: validateMessage.emailIsAlreadyRegistered })
              break
            case ErrorCode.DUPLICATE_CLICK_ID:
            case ErrorCode.DUPLICATE_GA_CLICK_ID:
              setError({
                icon: EmailIcon,
                message: validateMessage.youAlreadyHaveRegisteredAccount,
              })
              break
            case ErrorCode.CURRENCY_NOT_FOUND:
              setError({ message: validateMessage.currencyNotFound })
              break
            case ErrorCode.GEO_RESTRICTED:
              setError({ message: validateMessage.restricted })
              break
            case ErrorCode.SOCIAL_DUPLICATE_USER_EMAIL:
              setError({ icon: EmailIcon, message: validateMessage.duplicateUserEmail })
              break
            case ErrorCode.INCORRECT_MAIL_DOMAIN:
              setError({ icon: InvalidEmailIcon, message: t('error:emailDomainNotValid') })
              break
            default:
              setError({ icon: WiFiIcon, message: validateMessage.serverError })
          }

          setEvent(PlausibleEvent.NotRegistered, {
            type: 'system',
            step: 3,
            referrer,
            source: router.query?.from as string,
            error: ErrorCode[code] || 'unknown',
            userCurrency: values.currency.value,
          })
        } else {
          toggleIsSucceed(true)
          if (localStorage.getItem('registeringFromPopup')) {
            const propType = localStorage.getItem('registeringFromPopup')
            setEvent(PlausibleEvent.RegAfterClickOnPopup, {
              popupType: propType,
              referrer,
              locale: router.locale,
              domain: window.location.host,
            })
            localStorage.removeItem('registeringFromPopup')
          }

          await Promise.all([
            setEvent(
              PlausibleEvent.UserRegistered,
              {
                userId: res.data.payload?.id,
                referrer,
                source: window.location.host,
                type: RegistrationTypes.Google,
              },
              true,
            ),
            setEvent(
              PlausibleEvent.Registered,
              {
                type: 'system',
                referrer,
                step: 3,
                source: router.query?.from as string,
                userCurrency: values.currency.value,
              },
              true,
            ),
          ])

          setError(undefined)
          Cookies.set('justRegistered', 'true')
          localStorage.setItem('justRegistered', 'true')
          clearReferralCookies()
          setTokensAndRedirect(res.data.payload.accessToken, res.data.payload.refreshToken)
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)

        setError({ icon: WiFiIcon, message: validateMessage.serverError })
      } finally {
        toggleIsLoading(false)
      }
    },
    [setError, savedLanguage, initAntifraudSession],
  )

  return (
    <Formik
      initialValues={{ email: '', password: '', currency: undefined }}
      validationSchema={yup.object({
        email: yup
          .string()
          .email(t('error:emailNotValid'))
          .matches(latinWithSpecialSymbols, t('error:emailNotValid'))
          .required(t('error:requiredField'))
          .test('check-domain', t('error:emailDomainNotValid'), (value) => {
            if (value) {
              const [, domain] = value.split('@')
              return !emailsBlackList.includes(domain)
            }
            return true
          })
          .test('dash-cyrillic', t('error:emailDomainNotValid'), (value) => {
            if (value) {
              const [, domain] = value.split('@')
              const [, tld] = domain?.split('.') || []
              if (domain?.startsWith('xn--') || tld?.match(/[а-яА-Я-_\s]+/)) return false
            }
            return true
          })
          .max(49, t('error:maxLenSymbols', { length: 49 })),
        password: yup
          .string()
          .min(8, t('error:passwordMinChar'))
          .required(t('error:requiredField')),
        // .max(32, 'Maximum 32 characters')
        // .test('password', 'Invalid entry', (value) => {
        //   return (
        //     /[0-9]/.test(value) &&
        //     /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~№]/.test(value) &&
        //     !value.includes(' ') &&
        //     /[A-Z]/.test(value) &&
        //     /[a-z]/.test(value) &&
        //     onlyAllowedAndLatinLetters(value)
        //   )
        // })
        currency: yup.object().required(t('error:requiredField')),
      })}
      onSubmit={onRegister}
    >
      <RegisterFormInner
        currencies={currencyFieldValues}
        isCurrenciesloading={isCurrenciesloading}
        error={error}
        isLoading={isLoading}
        isSucceed={isSucceed}
        referrer={referrer}
      />
    </Formik>
  )
}

export default RegisterForm
